
import { Type } from "@google/genai";

// ==========================================
// 🧊 核心知识库 (v6.3 Schema Update)
// 包含核心岩石分类逻辑、经济地质及微观光薄片分析。
// ==========================================

export const CORE_LOGIC = {
  // 身份定义
  roleDefinition: `你是一位严谨的岩石学专家，精通火成岩、沉积岩与变质岩的鉴定。
  你擅长通过宏观手标本特征推断微观构造，并能识别复杂的蚀变与找矿标志。
  你的任务是提供客观、专业、去人设化的岩石鉴定报告。`,

  // 视觉校准规则 (Case 97-160+)
  visualRules: `
  === ⚠️ 视觉校准逻辑库 (v5.3.0 Frozen) ===
  **核心鉴定准则 (全岩石大类集成)：**

  [博物馆标本特殊协议 (Museum Specimen Protocol)]
  - **光影干扰**: 博物馆射灯常在平整断口产生“假丝绢光泽”。若岩石整体致密、块状，严禁因表面反光判定为千枚岩。
  - **节理 vs 片理**: 苦橄岩等基性岩常发育交叉节理（直线状裂隙），这与变质岩的波浪状片理有本质区别。

  [岩浆岩类：Case 97-125]
  - **苦橄岩 (Picrite) 专项判定 (MANDATORY CHECK)**: 
    - 视觉特征：深黑色、深绿色，致密块状构造。
    - 关键判据：寻找油脂光泽的粒状橄榄石斑晶。
    - **排他性逻辑**: 如果岩石看起来像千枚岩，但颜色极深且没有明显的薄片状剥落感，必须优先考虑苦橄岩。苦橄岩是火成岩，其“光泽”源于矿物解理或断口，而非变质云母。
  - **苦橄岩 (Picrite) vs 辉绿岩/辉长岩**: 苦橄岩为超基性岩，暗色矿物（尤其是橄榄石）含量极高(>50%)。若岩石呈深黑色、密度大、且含有明显的橄榄石斑晶（通常呈粒状、油脂光泽，风化后可能呈红褐色）或粒状橄榄石集合体，应优先判定为苦橄岩。
  - **斜长岩 vs 闪长岩**: 斜长岩为单矿物优势(>90%斜长石)，不可有均匀椒盐感。
  - **纯橄岩 vs 辉绿岩**: 纯橄岩为砂糖状结晶，无长石骨架。
  - **苏长岩 vs 闪长岩**: 苏长岩含斜方辉石，色率高，无角闪石为主的特征。
  - **二长岩/正长岩/英云闪长岩**: 严格按QAPF长石比例及石英含量区分。
  - **超基性系列**: 识别方辉橄榄岩、斜方辉石岩。

  [沉积岩类：Case 126-140]
  - **碎屑岩**: 区分长石砂岩(Arkose)、杂砂岩。砾岩与角砾岩按圆度仲裁。
  - **化石/生物类**: 识别壳灰岩、鲕粒灰岩、硅藻土。
  - **化学/特殊类**: 区分燧石、带状铁建造(BIF)、石膏、褐煤。

  [变质岩类：Case 141-160+]
  - **高压变质**: 榴辉岩(Grt+Omp)、蓝片岩(Glaucophane)。
  - **区域/深度变质**: 片麻岩(眼球状构造)、麻粒岩、混合岩、紫苏花岗岩。
  - **动力/热变质**: 糜棱岩、角岩、矽卡岩、云英岩。
  - **低级变质**: 千枚岩(丝绢光泽)、板岩(劈理)、绿岩(绿泥石化)。
  - **蛇纹岩/皂石**: 触感及滑腻感判定。

  **输出禁令**: 严禁提及 Case 编号。保持教授级专业口吻。
  `
};

// 核心输出 Schema v6.5
export const ROCK_SCHEMA = {
  type: Type.OBJECT,
  properties: {
    name: { type: Type.STRING, description: "岩石或矿物的具体名称。" },
    englishName: { type: Type.STRING, description: "岩石的标准英文名称 (例如: Granite, Basalt, Gneiss)。" },
    category: { type: Type.STRING, description: "分类归属。如：岩浆岩-深成、沉积岩-碎屑、变质岩等。" },
    isThinSection: { type: Type.BOOLEAN, description: "如果输入图像是显微镜下的光薄片(Micrograph/Thin Section)，必须设为 true。如果是手标本，设为 false。" },
    grainSize: { type: Type.STRING, description: "描述颗粒/晶体大小。" },
    textureAndStructure: { type: Type.STRING, description: "描述结构构造特征。" },
    minerals: { 
      type: Type.ARRAY, 
      items: { type: Type.STRING }, 
      description: "主要矿物成分。" 
    },
    hardness: { type: Type.STRING, description: "硬度范围预估 (莫氏硬度)。" },
    weathering: { type: Type.STRING, description: "风化表现。" },
    colorCause: { type: Type.STRING, description: "颜色及其对应的成因分析。" },
    alteration: { type: Type.STRING, description: "蚀变或交代情况 (简述)。" },
    metamorphism: { type: Type.STRING, description: "变质相带或岩石演化分析。" },
    formationReason: { type: Type.STRING, description: "命名逻辑。" },
    geologicalContext: { type: Type.STRING, description: "地质背景判据：引用专业术语。" },
    detailedDescription: { type: Type.STRING, description: "专业且优美的综合评价。" },
    confidenceScore: { type: Type.NUMBER, description: "鉴定置信度 (0-100)。" },
    diagnosticTests: {
      type: Type.ARRAY,
      description: "交互式诊断建议：针对当前鉴定结果，建议用户进行的物理测试（如硬度、酸反应等）。",
      items: {
        type: Type.OBJECT,
        properties: {
          test: { type: Type.STRING, description: "测试名称 (如：盐酸反应)。" },
          instruction: { type: Type.STRING, description: "操作指南 (如：滴稀盐酸在新鲜面上)。" },
          expected: { type: Type.STRING, description: "预期现象 (如：若剧烈起泡则为碳酸盐岩)。" }
        },
        required: ["test", "instruction", "expected"]
      }
    },
    secondPossibility: {
      type: Type.OBJECT,
      description: "第二种可能性。",
      properties: {
        name: { type: Type.STRING, description: "第二可能的名称" },
        reason: { type: Type.STRING, description: "推测理由及区分条件。" }
      },
      nullable: true
    },
    // v6.2: 经济地质
    economicAnalysis: {
      type: Type.OBJECT,
      description: "经济地质与找矿潜力分析",
      properties: {
        alterationMapping: { type: Type.STRING, description: "围岩蚀变识别 (如：高岭土化、硅化、青磐岩化) 及其找矿意义。" },
        mineralIndicators: { type: Type.STRING, description: "指示矿物 (如：黄铁矿、孔雀石、电气石) 及其指示的矿种。" },
        structuralControl: { type: Type.STRING, description: "构造控矿分析 (如：流体通道、角砾岩筒、剪切带)。" },
        prospectingPotential: { type: Type.STRING, description: "综合找矿潜力评价 (如：疑似铁帽、热液矿床围岩、无明显矿化等)。" }
      },
      required: ["alterationMapping", "mineralIndicators", "structuralControl", "prospectingPotential"]
    },
    // v6.3: 虚拟光薄片分析
    microscopicAnalysis: {
      type: Type.OBJECT,
      description: "推测该岩石在偏光显微镜下的特征 (Virtual Thin Section)",
      properties: {
        texture: { type: Type.STRING, description: "镜下结构术语 (如：花岗结构, 包含结构, 斑状结构)。" },
        xplFeatures: { type: Type.STRING, description: "正交偏光(XPL)下的特征 (如：石英波状消光, 斜长石聚片双晶, 干涉色等级)。" },
        pplFeatures: { type: Type.STRING, description: "单偏光(PPL)下的特征 (如：黑云母多色性, 角闪石解理夹角, 矿物突起)。" },
        inference: { type: Type.STRING, description: "宏观特征与微观特征的对应解读 (告诉学生为什么手标本这样长，镜下会那样长)。" }
      },
      required: ["texture", "xplFeatures", "pplFeatures", "inference"]
    },
    // v7.1: 古生物化石专项分析
    fossilAnalysis: {
      type: Type.OBJECT,
      description: "古生物化石专项分析。🚨绝对规则：仅当样本中存在明确无误的古生物化石时（如三叶虫、菊石、木化石等）才输出此项！如果是普通岩石（如纯橄岩、花岗岩等纯无机矿物岩石），此字段必须极其严格地返回 null。如果确实存在化石，则必须运用 [6. 🦖 古生物化石鉴定模块] 中的逻辑树进行深度解剖学特征描述。",
      properties: {
        fossilType: { type: Type.STRING, description: "化石类型 (如：植物化石, 动物化石, 遗迹化石)。" },
        genusSpecies: { type: Type.STRING, description: "属种建议 (强制调用化石模块的分类树，如：莱德利基虫, 武定虫, 云南头虫, 等)。" },
        geologicalAge: { type: Type.STRING, description: "地质年代建议 (如：古生代寒武纪, 中生代侏罗纪)。需配合化石特征和产地推断。绝对禁止输出“待分析”、“未知”等敷衍词汇，必须根据化石门类给出收敛的年代范围！" },
        preservation: { type: Type.STRING, description: "保存状态 (如：压印模铸, 碳质薄膜, 实体硅化)。绝对禁止输出“见图”、“待分析”等废话敷衍词汇，必须给出专业推断！" },
        environment: { type: Type.STRING, description: "古环境推断 (如：滨海台地微相, 深海缺氧碳质页岩环境)。绝对禁止只输出干瘪的“沉积环境”，必须结合化石种类给出具体的古生态环境！" },
        description: { type: Type.STRING, description: "核心指令：必须详尽且丰满地描述化石的解剖学特征！不仅要说明化石的分类，更要引用样本上的视觉细节（如：头甲比例、背壳上中轴与肋叶的明确界限、胸节数量和尖刺延伸等），请发挥你在化石鉴定模块中的毕生学绝学，将干瘪的描述化为博物馆级别的解剖学图谱描述。" }
      },
      required: ["fossilType", "genusSpecies", "geologicalAge", "preservation", "environment", "description"],
      nullable: true
    }
  },
  required: [
    "name", "englishName", "category", "isThinSection", "grainSize", "textureAndStructure", "minerals", 
    "hardness", "weathering", "colorCause", "alteration", "metamorphism", 
    "formationReason", "geologicalContext", "detailedDescription", "confidenceScore", "diagnosticTests", "secondPossibility",
    "economicAnalysis", "microscopicAnalysis"
  ]
};
